import { getCustomEvents } from 'api/CustomEventManagementAPI';
import { getDeletionPoliciesByProject, updateDeletionPolicies } from 'api/CustomEventsAPI';
import { addNegativeToast, addPositiveToast, removeAllToasts } from 'modules/toastContainer';
import { call, put, takeLatest } from 'redux-saga/effects';
import { addToRootSaga } from 'store';
import { getErrorResponse } from 'utils/xhr';
import * as actionTypes from './actionTypes';
import * as actions from './actions';
import { RateLimitedErrorToast } from './components/RateLimitedErrorToast';
import { DEBOUNCE_TIMEOUTS_MS } from './constants';
import {
  CustomEventsCacheType,
  CustomEventsRequestAction,
  DeletionPolicy,
  UpdateDeletionPoliciesAction,
} from './types';
import {
  createUpdateDeletionPolicyPayload,
  formatUpdateDeletionPolicyToastMessage,
  getEventNamesByRetentionPeriod,
} from './utils';

// redux-saga includes delay with v1, but we are not at that version yet
// https://redux-saga.js.org/docs/recipes/#debouncing
const delay = (ms: number) => {
  return new Promise((resolve) => setTimeout(() => resolve(true), ms));
};

const customEventsCache: CustomEventsCacheType = {};

export function* fetchCustomEventsSaga({ queryString, dispatch }: CustomEventsRequestAction) {
  try {
    yield put(removeAllToasts());
    const keyString = queryString || 'ALL_TIME';
    const cachedResponse = customEventsCache?.[`${keyString}`];

    if (cachedResponse) {
      yield put(actions.fetchCustomEventsResponse(cachedResponse));
    } else {
      const { data } = yield call(getCustomEvents, queryString);
      const isFirst = Object.keys(customEventsCache).length === 0;
      yield Object.assign(customEventsCache, { [keyString]: data });
      yield !isFirst && delay(DEBOUNCE_TIMEOUTS_MS.fetchCustomEvents);
      const customEvents = Array.isArray(data) ? data : [];
      yield put(actions.fetchCustomEventsResponse(customEvents));
    }
  } catch (error) {
    const err = getErrorResponse(error);
    yield put(actions.fetchCustomEventsError(err));
    yield put(
      addNegativeToast(<RateLimitedErrorToast />, {
        actions: [
          {
            title: 'Retry',
            onClick: () => dispatch(actions.fetchCustomEventsRequest(queryString, dispatch)),
          },
        ],
        noDelay: true,
      }),
    );
  }
}

export function* fetchDeletionPoliciesByProjectSaga() {
  try {
    const deletionPolicies: DeletionPolicy[] = yield call(getDeletionPoliciesByProject);
    const eventNamesByRetentionPeriod = getEventNamesByRetentionPeriod(deletionPolicies);
    yield put(actions.fetchDeletionPoliciesByProjectResponse(eventNamesByRetentionPeriod));
  } catch (error) {
    const err = getErrorResponse(error);
    yield put(actions.fetchDeletionPoliciesByProjectError(err));
    yield put(addNegativeToast(err));
  }
}

export function* updateDeletionPoliciesSaga({
  deletionPolicyUpdate,
}: UpdateDeletionPoliciesAction) {
  try {
    const { events, projectId, retentionPeriod, isInitial } = deletionPolicyUpdate;
    const positiveToastMessage = formatUpdateDeletionPolicyToastMessage(retentionPeriod, events);
    const payload = createUpdateDeletionPolicyPayload(retentionPeriod, events, projectId);

    yield call(updateDeletionPolicies, payload);
    yield !isInitial && put(addPositiveToast(positiveToastMessage));
    yield put(actions.updateDeletionPoliciesResponse());
    yield call(fetchDeletionPoliciesByProjectSaga);
  } catch (error) {
    const err = getErrorResponse(error);
    yield put(actions.updateDeletionPoliciesError(err));
    yield put(addNegativeToast(err));
  }
}

addToRootSaga([
  takeLatest(
    actionTypes.FETCH_DELETION_POLICIES_BY_PROJECT_REQUEST,
    fetchDeletionPoliciesByProjectSaga,
  ),
  takeLatest(actionTypes.UPDATE_DELETION_POLICIES_REQUEST, updateDeletionPoliciesSaga),
  takeLatest(actionTypes.FETCH_CUSTOM_EVENTS_REQUEST, fetchCustomEventsSaga),
]);
